import { useEffect, useState } from "react";
import { Select } from "@rio-cloud/rio-uikit";
import ErrorDialog from "../../../../../dialogs/ErrorDialog";
import SuccessDialog from "../../../../../dialogs/SuccessDialog";
import {
  Occurrence,
  OccurrenceDispatch,
} from "../../../../../../models/occurrence";
import { useDispatchForm } from "../../../../../../hooks/useDispatch";
import ChooseDealershipDialog from "./dialogs/chooseDealershipDialog";
import useAssets from "../../../../../../hooks/useAssets";
import { useIntl } from "react-intl";

interface DispatchFormProps {
  occurrence?: Occurrence | null;
  upsertDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch
  ) => Promise<{
    wasCreated: boolean;
    error?: string;
    dispatch?: OccurrenceDispatch;
  }>;
  updateDispatchList: (dispatch: OccurrenceDispatch) => void;
  dispatches: OccurrenceDispatch[];
  persona?: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  clickedChassis: string | undefined;
  removeDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<void>;
}

const DispatchForm = (props: DispatchFormProps) => {
  const {
    optionsOccurrenceType,
    optionsPayer,
    optionsReleasePayment,
    loading,
    upsertDispatchLoading,
    showErrorDialog,
    errorDialogBody,
    showSuccessDialog,
    dispatchForm,
    setDispatchForm,
    setShowErrorDialog,
    setShowSuccessDialog,
    handleAddDispatch,
    handleUpdateDispatch,
    showChooseDealershipDialog,
    setShowChooseDealershipDialog,
  } = useDispatchForm(props);
  
  const { getAssets, getAssetIdByChassis } = useAssets();
  
  const intl = useIntl();
  
  const [assetId, setAssetId] = useState<string | undefined>(undefined);

  useEffect(() => {
    getAssets().then(() => {
      const id = getAssetIdByChassis(props.clickedChassis ?? "");
      setAssetId(id);
    });
  }, [getAssets, getAssetIdByChassis, props.clickedChassis]);

  return (
    <>
      <div className="flex justify-between w-full gap-6 items-end my-6 p-6 pb-0 border rounded">
        <div className="form-group form-group-lg w-full">
          <label className="truncate">{intl.formatMessage({ id: 'intl-msg:dispatch.occurrence-type' })}*</label>
          <Select
            id="1"
            placeholder={intl.formatMessage({ id: 'intl-msg:dispatch.occurrence-type-placeholder' })}
            value={[dispatchForm.occurrenceType ?? ""]}
            options={optionsOccurrenceType}
            onChange={(value) =>
              setDispatchForm({ ...dispatchForm, occurrenceType: value?.id })
            }
          />
        </div>

        <div className="form-group form-group-lg w-full">
          <label className="truncate">{intl.formatMessage({ id: 'intl-msg:dispatch.payer' })}*</label>
          <Select
            id="1"
            placeholder={intl.formatMessage({ id: 'intl-msg:dispatch.payer-placeholder' })}
            value={[dispatchForm.payer ?? ""]}
            options={optionsPayer}
            onChange={(value) =>
              setDispatchForm({ ...dispatchForm, payer: value?.id })
            }
          />
        </div>

        <div className="form-group form-group-lg w-full">
          <label className="truncate">{intl.formatMessage({ id: 'intl-msg:dispatch.release-payment' })}*</label>
          <Select
            id="1"
            placeholder={intl.formatMessage({ id: 'intl-msg:dispatch.release-payment-placeholder' })}
            value={[dispatchForm.releasePayment ?? ""]}
            options={optionsReleasePayment}
            onChange={(value) =>
              setDispatchForm({ ...dispatchForm, releasePayment: value?.id })
            }
          />
        </div>

        <div className="form-group form-group-lg w-full">
          <button
            className={`btn btn-lg btn-default w-full ${
              loading || upsertDispatchLoading ? "btn-loading disabled" : ""
            }`}
            disabled={
              loading ||
              upsertDispatchLoading ||
              !dispatchForm.occurrenceType ||
              !dispatchForm.payer ||
              !dispatchForm.releasePayment
            }
            onClick={async () => {
              const wasCreated = await handleAddDispatch(props.occurrence?.dealershipDn ?? "");
              if (wasCreated) {
                setShowChooseDealershipDialog(true);
              }
            }}
          >
            {upsertDispatchLoading ? intl.formatMessage({ id: 'intl-msg:dispatch.creating' }) : intl.formatMessage({ id: 'intl-msg:dispatch.assigning' })}
          </button>
        </div>
      </div>

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title={intl.formatMessage({ id: 'intl-msg:dispatch.error.title' })}
        body={errorDialogBody}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={intl.formatMessage({ id: 'intl-msg:dispatch.success.title' })}
        body={intl.formatMessage({ id: 'intl-msg:dispatch.success.body' })}
      />

      <ChooseDealershipDialog
        show={showChooseDealershipDialog}
        onClose={async () => {
          setShowChooseDealershipDialog(false)
          setDispatchForm({
            id: undefined,
            occurrenceType: undefined,
            payer: undefined,
            releasePayment: undefined,
          })
          if (props.occurrence?.id && dispatchForm.id) {
            await props.removeDispatch?.(props.occurrence, dispatchForm);
          }
        }}
        onChooseDealership={async (dn: string, route: string, originLocation: string, destinationLocation: string) => {
          setShowChooseDealershipDialog(false);
          await handleUpdateDispatch(dn, route, originLocation, destinationLocation);
          setDispatchForm({
            id: undefined,
            occurrenceType: undefined,
            payer: undefined,
            releasePayment: undefined,
          })
        }}
        occurrenceType={dispatchForm.occurrenceType}
        assetId={assetId ?? ""}
        chassis={props.clickedChassis}
        ocurrenceId={props.occurrence?.id}
        vehicleName={props.occurrence?.vehicleName}
        dispatchId={dispatchForm.id}
      />
    </>
  );
};

export default DispatchForm;
